import { formatLocalDateTime } from '@utils/dateUtils';

/* -----------------------------------------------------
   Constants
----------------------------------------------------- */
export const VANCE_FX_TABLE_HEADERS = [
  { key: 'currency', label: 'Base Currency' },
  { key: 'quoteCurrency', label: 'Quote Currency' },
  { key: 'vanceFxRate', label: 'Vance FX Rate' },
  { key: 'effectiveProvider', label: 'Effective Provider' },
  { key: 'storedAt', label: 'Stored At' },
  { key: 'fetchedAt', label: 'Fetched At' },
  { key: 'remarks', label: 'Remarks' },
  { key: 'rawGoogleFxRate', label: 'Raw Google FX Rate' },
  { key: 'normalizedGoogleFxRate', label: 'Normalized Google FX Rate' },
  { key: 'marketRate', label: 'Market Rate' },
  { key: 'customRate', label: 'Custom Rate' },
];

export const PREFERRED_CURRENCY_ORDER = ['GBP', 'AED', 'USD', 'EUR'];

/* -----------------------------------------------------
   Utility Functions
----------------------------------------------------- */

/**
 * Returns the badge color scheme for a given FX rate provider
 * @param {string} provider - The provider name
 * @returns {string} - Chakra UI color scheme
 */
export const getProviderBadgeColor = (provider) => {
  switch (provider?.toLowerCase()) {
    case 'google_rates':
      return 'green';
    case 'market_rates':
      return 'green';
    case 'custom_rates':
      return 'purple';
    default:
      return 'gray';
  }
};

/**
 * Renders a cell value with appropriate formatting based on the column key
 * @param {string} key - The column key
 * @param {any} value - The cell value
 * @param {object} rowData - The full row data (unused but available for future extensions)
 * @param {Function} BadgeComponent - Badge component for rendering provider badges
 * @returns {any} - Formatted cell value
 */
export const renderCellValue = (key, value, rowData, BadgeComponent) => {
  switch (key) {
    case 'vanceFxRate':
    case 'rawGoogleFxRate':
    case 'normalizedGoogleFxRate':
    case 'marketRate':
    case 'customRate':
      return value;
    case 'storedAt':
    case 'fetchedAt':
      return formatLocalDateTime(value);
    case 'effectiveProvider':
      return value && BadgeComponent ? BadgeComponent : (value || '-');
    case 'remarks':
      return value || '-';
    default:
      return value ?? '-';
  }
};

/**
 * Sorts currencies based on a preferred order, with remaining currencies sorted alphabetically
 * @param {string[]} currencies - Array of currency codes
 * @param {string[]} preferredOrder - Array of currency codes in preferred order
 * @returns {string[]} - Sorted array of currency codes
 */
export const sortCurrencies = (currencies, preferredOrder = PREFERRED_CURRENCY_ORDER) => {
  return [...currencies].sort((a, b) => {
    const indexA = preferredOrder.indexOf(a);
    const indexB = preferredOrder.indexOf(b);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return a.localeCompare(b);
  });
};
